import React, { type MouseEvent, type SyntheticEvent } from 'react';
import Button from '@atlaskit/button';
import ModalDialog, {
	ModalHeader,
	ModalTitle,
	ModalBody,
	ModalFooter,
	ModalTransition,
	type OnCloseHandler,
} from '@atlaskit/modal-dialog';
import { ShortcutScope } from '@atlassian/jira-common-components-keyboard-shortcuts';
import { FormattedMessage } from '@atlassian/jira-intl';

type Props = {
	modalOpen: boolean;
	onCancelDelete: OnCloseHandler;
	onConfirmDelete: (
		event: MouseEvent<HTMLElement, globalThis.MouseEvent> | SyntheticEvent<Element, Event>,
	) => void;
	title?: string | React.ReactNode;
	description?: string | React.ReactNode;
};

export const ConfirmationModal = ({
	modalOpen,
	onCancelDelete,
	onConfirmDelete,
	title,
	description,
}: Props) => (
	<ModalTransition>
		{modalOpen && (
			<ShortcutScope>
				<ModalDialog
					testId="issue-view-common-views.confluence-content-line-card.ui.confirmation-modal"
					onClose={onCancelDelete}
					width="small"
				>
					<ModalHeader>
						<ModalTitle appearance="danger">
							{title || (
								<FormattedMessage
									id="issue.details.confluence-pages.delete-confirmation-modal.title"
									defaultMessage="Delete the Confluence page link?"
								/>
							)}
						</ModalTitle>
					</ModalHeader>
					<ModalBody>
						{description || (
							<FormattedMessage
								id="issue.details.confluence-pages.delete-confirmation-modal.content"
								defaultMessage="You can add the page later again if you need to."
							/>
						)}
					</ModalBody>
					<ModalFooter>
						<Button
							appearance="danger"
							onClick={onConfirmDelete}
							/* eslint-disable-next-line jira/integration/test-id-by-folder-structure */
							testId="issue.views.common.confluence-page-line-card.confirm-button"
							interactionName="auto-generated.issue.views.common.confluence-page-line-card.confirm-button.button"
						>
							<FormattedMessage
								id="issue.details.confluence-pages.delete-confirmation-modal.confirmation-text"
								defaultMessage="Delete"
							/>
						</Button>
						<Button
							appearance="subtle"
							onClick={onCancelDelete}
							/* eslint-disable-next-line jira/integration/test-id-by-folder-structure */
							testId="issue.views.common.confluence-page-line-card.cancel-button"
							interactionName="auto-generated.issue.views.common.confluence-page-line-card.cancel-button.button1"
						>
							<FormattedMessage
								id="issue.details.confluence-pages.delete-confirmation-modal.cancellation-text"
								defaultMessage="Cancel"
							/>
						</Button>
					</ModalFooter>
				</ModalDialog>
			</ShortcutScope>
		)}
	</ModalTransition>
);
